import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
import { useTheme } from '../contexts/ThemeContext';
import { matchesSearch } from '../common/search';
import { formatDurationLabel, getTaskTimeUnits } from '../common/taskProgress';

const { FiClock, FiFolders, FiCalendar, FiFlag, FiMove } = FiIcons;

const TimeboxingView = ({
  tasks,
  projects,
  currentProjectId,
  onTaskEdit,
  updateTaskDates,
  onToggleComplete,
  searchQuery,
  tags
}) => {
  const { t } = useTranslation();
  const [viewType, setViewType] = useState('timeGridDay');
  const [unscheduledSort, setUnscheduledSort] = useState('priority');
  const calendarRef = useRef(null);
  const { darkMode } = useTheme();

  // Filter tasks by project if a project is selected
  const filteredTasks = currentProjectId
    ? tasks.filter(task => task.projectId === currentProjectId)
    : tasks;

  // Split tasks into scheduled and unscheduled
  const unscheduledMain = filteredTasks.filter(task =>
    !task.completed &&
    !task.isMultiDay &&
    (!task.dueDate || !task.startTime) &&
    !(task.flexMode && task.subTasks && task.subTasks.length > 0)
  );
  const unscheduledSubs = filteredTasks.flatMap(t =>
    t.flexMode && !t.isMultiDay
      ? (t.subTasks || [])
          .filter(st => !st.dueDate && !st.isMultiDay)
          .map(st => ({
            ...st,
            parentTaskId: t.id,
            parentTitle: t.title,
            projectId: t.projectId,
            isSubtask: true,
            parentFlexMode: true
          }))
      : []
  );
  const unscheduledItems = [
    ...unscheduledMain.map(t => ({ ...t, isSubtask: false, flexMode: t.flexMode })),
    ...unscheduledSubs
  ];

  const [unscheduledFilter, setUnscheduledFilter] = useState('');
  const priorityRank = { high: 0, medium: 1, low: 2 };
  const displayedUnscheduled = unscheduledItems
    .filter(item =>
      (item.title + ' ' + (item.parentTitle || '')).toLowerCase().includes(unscheduledFilter.toLowerCase())
    )
    .sort((a, b) => {
      if (unscheduledSort === 'duration') {
        return (b.duration || 0) - (a.duration || 0);
      }

      if (unscheduledSort === 'created') {
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }

      return (priorityRank[a.priority] ?? 3) - (priorityRank[b.priority] ?? 3);
    });

  const calendarEvents = [];

  filteredTasks.forEach(task => {
    const project = projects.find(p => p.id === task.projectId);
    const projectColor = project?.color || '#3b82f6';
    const isMatch = matchesSearch(task, searchQuery, tags);

    if (task.dueDate && (task.startTime || task.isMultiDay) && !task.flexMode) {
      const startDate = new Date(task.dueDate);
      let endDate;
      if (task.isMultiDay && task.endDate) {
        endDate = new Date(task.endDate);
        endDate.setDate(endDate.getDate() + 1);
      } else if (task.startTime) {
        endDate = new Date(startDate);
        const [startHours, startMinutes] = task.startTime.split(':').map(Number);
        startDate.setHours(startHours, startMinutes, 0);
        endDate.setHours(startHours, startMinutes + (task.duration || 60), 0);
      } else {
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 1);
      }

      calendarEvents.push({
        id: task.id,
        title: task.title,
        start: startDate,
        end: endDate,
        backgroundColor: projectColor,
        borderColor: projectColor,
        textColor: '#ffffff',
        allDay: task.isMultiDay,
        editable: true,
        startEditable: true,
        durationEditable: true,
        classNames: isMatch ? ['search-highlight'] : [],
        extendedProps: {
          description: task.description,
          priority: task.priority,
          completed: task.completed,
          projectId: task.projectId,
          projectName: project?.name || 'Inbox',
          task: task
        }
      });
    }

    if (task.flexMode && task.subTasks && task.subTasks.length > 0) {
      task.subTasks.forEach(st => {
        if (!st.dueDate) return;
        const subStart = new Date(st.dueDate);
        let subEnd;
        if (st.isMultiDay && st.endDate) {
          subEnd = new Date(st.endDate);
          subEnd.setDate(subEnd.getDate() + 1);
        } else {
          const [h, m] = (st.time || '00:00').split(':').map(Number);
          subStart.setHours(h, m, 0, 0);
          subEnd = new Date(subStart);
          subEnd.setMinutes(subEnd.getMinutes() + (st.duration || 0));
          if (!task.flexMode) {
            const parentEnd = task.endDate ? new Date(task.endDate) : null;
            if (parentEnd && subEnd > parentEnd) subEnd.setTime(parentEnd.getTime());
          }
        }

        const subMatch = matchesSearch(st, searchQuery, tags);
        calendarEvents.push({
          id: st.id,
          title: st.title,
          start: subStart,
          end: subEnd,
          backgroundColor: projectColor,
          borderColor: projectColor,
          textColor: '#ffffff',
          allDay: st.isMultiDay || false,
          editable: true,
          startEditable: true,
          durationEditable: true,
          classNames: subMatch ? ['search-highlight'] : [],
          extendedProps: { task: st, parentTaskId: task.id, parentTitle: task.title }
        });
      });
    }
  });

  // Handle event click
  const handleEventClick = (info) => {
    const eventTask = info.event.extendedProps.task;
    const parentId = info.event.extendedProps.parentTaskId;
    if (parentId) {
      const parent = tasks.find(t => t.id === parentId);
      if (parent) onTaskEdit(parent, eventTask.id);
    } else {
      onTaskEdit(eventTask);
    }
  };

  // Normalize a date to the nearest 30-minute mark
  const normalizeDate = (date) => {
    const d = new Date(date);
    d.setSeconds(0, 0);
    const minutes = d.getMinutes();
    d.setMinutes(minutes - (minutes % 30));
    return d;
  };

  const hasOverlap = (taskId, startDate, endDate) => {
    const moving = tasks.find(t => t.id === taskId);
    const movingAnti = moving?.antiOverlap;
    const dayStr = startDate.toISOString().split('T')[0];
    const startMins = startDate.getHours() * 60 + startDate.getMinutes();
    const endMins = endDate.getHours() * 60 + endDate.getMinutes();
    return tasks.some(t => {
      if (
        t.id === taskId ||
        t.flexMode ||
        t.isMultiDay ||
        t.dueDate?.split('T')[0] !== dayStr ||
        !t.startTime ||
        !t.duration
      ) {
        return false;
      }
      const [h, m] = t.startTime.split(':').map(Number);
      const tStart = h * 60 + m;
      const tEnd = tStart + t.duration;
      const overlap = startMins < tEnd && endMins > tStart;
      return overlap && (movingAnti || t.antiOverlap);
    });
  };

  const renderEventContent = (eventInfo) => {
    const task = eventInfo.event.extendedProps.task;
    const parentTitle = eventInfo.event.extendedProps.parentTitle;
    const parentFlex = eventInfo.event.extendedProps.parentTaskId
      ? tasks.find(t => t.id === eventInfo.event.extendedProps.parentTaskId)?.flexMode
      : task.flexMode;
    const durationLabel = formatDurationLabel(task.duration);
    const taskProgress = getTaskTimeUnits(task);

    return (
      <div className={`fc-event-card ${task.completed ? 'is-completed' : ''}`}>
        <div className="fc-event-card-header">
          <input
            type="checkbox"
            checked={task.completed}
            onChange={() => onToggleComplete(task.id)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            className="fc-event-checkbox"
          />
          {parentTitle && <span className="fc-event-parent-marker">↳</span>}
          <span className="fc-event-title-text">{eventInfo.event.title}</span>
        </div>

        {parentTitle && <div className="fc-event-meta">{parentTitle}</div>}

        <div className="fc-event-badges">
          {task.priority === 'high' && (
            <span className="fc-event-badge fc-event-badge-high">
              <SafeIcon icon={FiFlag} />
              {t('timeboxing.highPriority')}
            </span>
          )}
          {durationLabel && (
            <span className="fc-event-badge">
              <SafeIcon icon={FiClock} />
              {durationLabel}
            </span>
          )}
          {taskProgress && (
            <span
              className="fc-event-badge"
              title={t('tasks.timeUnitsTooltip', { score: taskProgress.tuScore })}
            >
              {t(`tasks.timeUnits${taskProgress.tuStatus}`, {
                score: Math.abs(taskProgress.tuScore)
              })}
            </span>
          )}
        </div>

        {!parentTitle && task.subTasks && task.subTasks.length > 0 && !task.flexMode && (
          <div
            className="fc-event-subtasks"
            title={task.subTasks.map(st => st.title).join('\n')}
          >
            {task.subTasks.slice(0, 2).map(st => (
              <label
                key={st.id}
                className="fc-event-subtask"
                onClick={e => e.stopPropagation()}
                onMouseDown={e => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={st.completed}
                  onChange={() => onToggleComplete(st.id)}
                />
                <span>{st.title}</span>
              </label>
            ))}
            {task.subTasks.length > 2 && (
              <div className="fc-event-more">
                {t('tasks.moreSubtasks', { count: task.subTasks.length - 2 })}
              </div>
            )}
          </div>
        )}

        {parentFlex && (
          <div className="fc-event-meta text-purple-100">{t('timeboxing.flexModeActivated')}</div>
        )}
      </div>
    );
  };

  // Initialize FullCalendar external dragging for unscheduled tasks
  useEffect(() => {
    const containerEl = document.getElementById('external-task-list');
    let draggable;
    if (containerEl) {
      draggable = new Draggable(containerEl, {
        itemSelector: '.fc-draggable-task',
        eventData: function (taskEl) {
          const taskId = taskEl.getAttribute('data-task-id');
          const task = unscheduledItems.find(t => t.id === taskId);
          const project = projects.find(p => p.id === task?.projectId);
          const projectColor = project?.color || '#3b82f6';
          return {
            id: taskId,
            title: task?.title || '',
            backgroundColor: projectColor,
            borderColor: projectColor,
            extendedProps: { task, parentTaskId: task?.parentTaskId, parentTitle: task?.parentTitle }
          };
        }
      });
    }
    return () => {
      if (draggable) draggable.destroy();
    };
  }, [displayedUnscheduled, projects]);


  return (
    <div className="max-w-full mx-auto">
      <div className="mb-4 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-1">{t('timeboxing.title')}</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {t('timeboxing.subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <SafeIcon icon={FiCalendar} className="text-slate-500 dark:text-slate-400" />
          <select
            value={viewType}
            onChange={(e) => setViewType(e.target.value)}
            className="px-3 py-1.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="timeGridDay">{t('timeboxing.viewDay')}</option>
            <option value="timeGridWeek">{t('timeboxing.viewWeek')}</option>
          </select>
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiFolders} className="text-slate-500 dark:text-slate-400" />
            <span className="text-slate-600 dark:text-slate-400 text-sm">
              {currentProjectId
                ? t('timeboxing.showingProject', { projectName: projects.find(p => p.id === currentProjectId)?.name || 'Unknown Project' })
                : t('timeboxing.showingAllProjects')}
            </span>
          </div>
        </div>
      </div>

      {/* Timeboxing Layout */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Task List */}
        <div className="md:col-span-1">
          <motion.div
            id="external-task-list"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-soft border border-slate-200/60 dark:border-slate-700/60 h-[calc(100vh-100px)] overflow-y-auto"
          >
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
              <SafeIcon icon={FiClock} />
              {t('timeboxing.unscheduledTasks')}
            </h2>

            <input
              type="text"
              value={unscheduledFilter}
              onChange={(e) => setUnscheduledFilter(e.target.value)}
              placeholder={t('timeboxing.filterPlaceholder')}
              className="w-full mb-3 px-3 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm"
            />
            <select
              value={unscheduledSort}
              onChange={(e) => setUnscheduledSort(e.target.value)}
              className="w-full mb-3 px-3 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm"
            >
              <option value="priority">{t('timeboxing.sortPriority')}</option>
              <option value="duration">{t('timeboxing.sortDuration')}</option>
              <option value="created">{t('timeboxing.sortCreated')}</option>
            </select>

            <div className="space-y-3">
              {displayedUnscheduled.length === 0 ? (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <p>{t('timeboxing.noUnscheduledTasks')}</p>
                </div>
              ) : (
                displayedUnscheduled.map((task) => {
                  const project = projects.find(p => p.id === task.projectId);
                  const projectColor = project?.color || '#3b82f6';
                  const highlight = matchesSearch(task, searchQuery, tags)
                    ? 'search-highlight'
                    : '';

                  return (
                    <div
                      key={task.id}
                      className={`fc-draggable-task group ${task.isSubtask ? 'pl-2 border-l-2' : 'border-l-4'} bg-white dark:bg-slate-700 rounded-lg p-3 border border-slate-200 dark:border-slate-600 shadow-soft hover:shadow-medium hover:-translate-y-0.5 transition-all ${highlight}`}
                      data-task-id={task.id}
                      style={{ borderLeftColor: projectColor }}
                      onClick={() => {
                        if (task.isSubtask) {
                          const parent = tasks.find(t => t.id === task.parentTaskId);
                          if (parent) onTaskEdit(parent, task.id);
                        } else {
                          onTaskEdit(task);
                        }
                      }}
                    >
                      <div className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={task.completed}
                          onChange={() => onToggleComplete(task.id)}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-medium text-slate-800 dark:text-white truncate">
                              {task.isSubtask ? `↳ ${task.title}` : task.title}
                            </h3>
                            <SafeIcon icon={FiMove} className="text-slate-300 group-hover:text-blue-500 shrink-0" />
                          </div>
                          {task.parentTitle && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{task.parentTitle}</p>
                          )}
                          {task.description && !task.isSubtask && (
                            <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                              {task.description}
                            </p>
                          )}
                          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-slate-500 dark:text-slate-400">
                            <span>{project?.name || 'Inbox'}</span>
                            {task.priority && (
                              <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-600">
                                {task.priority}
                              </span>
                            )}
                            {task.duration && (
                              <span className="px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300">
                                {formatDurationLabel(task.duration)}
                              </span>
                            )}
                            {(task.flexMode || task.parentFlexMode) && (
                              <span className="px-2 py-0.5 rounded-full bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300">
                                {t('timeboxing.flexModeActivated')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        </div>

        {/* Calendar */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="md:col-span-2 bg-white dark:bg-slate-800 rounded-xl p-4 shadow-soft border border-slate-200/60 dark:border-slate-700/60 h-[calc(100vh-100px)] overflow-y-auto"
        >
          <div className={`calendar-container ${darkMode ? 'calendar-dark-mode' : ''}`}>
            <FullCalendar
              ref={calendarRef}
              plugins={[timeGridPlugin, interactionPlugin]}
              initialView={viewType}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'timeGridDay,timeGridWeek'
              }}
              events={calendarEvents}
              eventClick={handleEventClick}
              editable={true}
              droppable={true}
              eventStartEditable={true}
              eventDurationEditable={true}
              eventResizableFromStart={true}
              eventOverlap={(still, moving) => {
                const a = still.extendedProps.parentTaskId;
                const b = moving.extendedProps.parentTaskId;
                if (a && b && a === b) return false;
                return true;
              }}
              eventReceive={(info) => {
                const start = normalizeDate(info.event.start);
                const end = normalizeDate(
                  info.event.end || new Date(start.getTime() + (info.event.extendedProps.task?.duration || 60) * 60000)
                );
                if (hasOverlap(info.event.id, start, end)) {
                  alert(t('timeboxing.overlapAlert'));
                  info.event.remove();
                  return;
                }
                updateTaskDates(info.event.id, start, end);
                info.event.remove();
              }}
              eventDragStart={() => {
                const listEl = document.getElementById('external-task-list');
                if (listEl) {
                  listEl.classList.add('drop-target');
                }
              }}
              eventDragStop={(info) => {
                const listEl = document.getElementById('external-task-list');
                if (listEl) {
                  listEl.classList.remove('drop-target');
                  const rect = listEl.getBoundingClientRect();
                  const { clientX, clientY } = info.jsEvent;
                  if (
                    clientX >= rect.left &&
                    clientX <= rect.right &&
                    clientY >= rect.top &&
                    clientY <= rect.bottom
                  ) {
                    const parentId = info.event.extendedProps.parentTaskId;
                    const parent = parentId ? tasks.find(t => t.id === parentId) : null;
                    if (!parentId || parent?.flexMode) {
                      updateTaskDates(info.event.id, null, null);
                      info.event.remove();
                    }
                  }
                }
              }}
              eventDrop={(info) => {
                const taskId = info.event.id;
                const newStartDate = normalizeDate(info.event.start);
                const newEndDate = normalizeDate(
                  info.event.end || new Date(info.event.start.getTime() + (info.event.extendedProps.task?.duration || 60) * 60000)
                );
                if (hasOverlap(taskId, newStartDate, newEndDate)) {
                  info.revert();
                  alert(t('timeboxing.overlapAlert'));
                  return;
                }
                updateTaskDates(taskId, newStartDate, newEndDate);
              }}
              eventResize={(info) => {
                const taskId = info.event.id;
                const newStartDate = normalizeDate(info.event.start);
                const newEndDate = normalizeDate(info.event.end);
                if (hasOverlap(taskId, newStartDate, newEndDate)) {
                  info.revert();
                  alert(t('timeboxing.overlapAlert'));
                  return;
                }
                updateTaskDates(taskId, newStartDate, newEndDate);
              }}
              height="100%"
              slotMinTime="06:00:00"
              slotMaxTime="22:00:00"
              slotDuration="00:30:00"
              snapDuration="00:30:00"
              allDaySlot={true}
              nowIndicator={true}
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                meridiem: 'short'
              }}
              eventContent={renderEventContent}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TimeboxingView;
